import { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, FormHelperText, InputAdornment, InputLabel, OutlinedInput } from "@mui/material";
import * as yup from "yup";
import { atualizarPrecoSchema } from "../../schema/atualizarPrecoSchema";

export interface AtualizaPrecoProps {
    atualizar: (preco: string, precoSemDesconto?: string) => void;
    aberto: boolean;
    fechar: () => void;
}

export const ModalAtualizaPreco = ({
    atualizar,
    fechar,
    aberto
}: AtualizaPrecoProps) => {
    const [preco, setPreco] = useState("");
    const [precoSemDesconto, setPrecoSemDesconto] = useState("");
    const [erro, setErro] = useState<string | null>(null);
    const [erroSemDesconto, setErroSemDesconto] = useState<string | null>(null);

    const validatePreco = async (value: string, setErroCallback: (msg: string | null) => void) => {
        try {
            await atualizarPrecoSchema.validate({ preco: value });
            setErroCallback(null);
            return true;
        } catch (validationError) {
            setErroCallback((validationError as yup.ValidationError).message);
            return false;
        }
    };

    const handleFechar = () => {
        setPreco("");
        setPrecoSemDesconto("");
        setErro(null);
        setErroSemDesconto(null);
        fechar();
    };

    const handleAtualizar = async () => {
        const isValidPreco = await validatePreco(preco, setErro);
        const isValidPrecoSemDesconto = precoSemDesconto
            ? await validatePreco(precoSemDesconto, setErroSemDesconto)
            : true;

        if (!isValidPreco || !isValidPrecoSemDesconto) return;

        atualizar(preco, precoSemDesconto || undefined);
        handleFechar();
    };

    return (
        <Dialog
            open={aberto}
            onClose={handleFechar}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">Atualizar preço</DialogTitle>
            <DialogContent sx={{ minWidth: "450px" }}>
                <DialogContentText id="alert-dialog-description">
                    {/* Campo Preço */}
                    <FormControl fullWidth sx={{ mt: 3 }} error={!!erro}>
                        <InputLabel htmlFor="preco">Preço</InputLabel>
                        <OutlinedInput
                            id="preco"
                            startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                            label="Preço"
                            value={preco}
                            onChange={(e) => {
                                setPreco(e.target.value);
                                setErro(null);
                            }}
                            onBlur={() => validatePreco(preco, setErro)}
                        />
                        {erro && <FormHelperText>{erro}</FormHelperText>}
                    </FormControl>

                    {/* Campo Preço sem Desconto */}
                    <FormControl fullWidth sx={{ mt: 3 }} error={!!erroSemDesconto}>
                        <InputLabel htmlFor="preco-sem-desconto">Preço sem desconto</InputLabel>
                        <OutlinedInput
                            id="preco-sem-desconto"
                            startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                            label="Preço sem desconto"
                            value={precoSemDesconto}
                            onChange={(e) => {
                                setPrecoSemDesconto(e.target.value);
                                setErroSemDesconto(null);
                            }}
                            onBlur={() => precoSemDesconto && validatePreco(precoSemDesconto, setErroSemDesconto)}
                        />
                        {erroSemDesconto && <FormHelperText>{erroSemDesconto}</FormHelperText>}
                    </FormControl>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' onClick={handleFechar} color="primary">
                    Cancelar
                </Button>
                <Button 
                    variant='contained' 
                    onClick={handleAtualizar} 
                    color="primary" 
                    disabled={!!erro || !preco || (!!precoSemDesconto && !!erroSemDesconto)}
                >
                    Confirmar
                </Button>
            </DialogActions>
        </Dialog>
    );
};
